import { useRef, useMemo, useEffect, useCallback, forwardRef } from 'react';
import { hu } from 'date-fns/locale';
import { TextField, MenuItem } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core/styles';
import DateFnsUtils from '@date-io/date-fns';

import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';
import { getSummaryResolution, parseTsToDate, toLocalDayDate, getDateKey, getTsCacheKey, sortTimestampsDesc, buildTimestampIndex } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/dateTime';

const FIELD_HEIGHT = 56;

const useHeaderDateTimeStyles = makeStyles(() => ({
    datePickerRoot: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff'
        },
        '& .MuiOutlinedInput-root.Mui-focused': {
            backgroundColor: '#ffffff !important'
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input': {
            backgroundColor: '#ffffff'
        },
        '& .MuiOutlinedInput-adornedEnd': {
            paddingRight: 10
        }
    },
    datePickerInput: {
        padding: '18.5px 14px'
    },
    datePickerAdornment: {
        marginRight: 0,
        '& .MuiIconButton-root': {
            padding: 4,
            marginRight: 0,
        }
    },
    timeFieldRoot: {
        '& .MuiOutlinedInput-root': {
            height: FIELD_HEIGHT,
            backgroundColor: '#ffffff'
        },
        '& .MuiOutlinedInput-root.Mui-focused': {
            backgroundColor: '#ffffff !important'
        },
        '& .MuiOutlinedInput-input': {
            height: FIELD_HEIGHT,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            padding: 0
        },
        '& .MuiSelect-outlined': {
            height: FIELD_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            paddingTop: 0,
            paddingBottom: 0,
            paddingRight: 32,
            paddingLeft: 0,
            boxSizing: 'border-box'
        },
        '& .MuiOutlinedInput-adornedStart': {
            paddingLeft: 12
        },
        '& .MuiInputAdornment-root': {
            height: FIELD_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            marginRight: 8
        }
    }
}));

const HeaderDateTextField = forwardRef(function HeaderDateTextField(props, ref) {
    const { pickerClasses, inputRef, InputProps, className, ...rest } = props;

    return (
        <TextField
            {...rest}
            ref={ref}
            inputRef={inputRef}
            size='small'
            className={(className ? className + ' ' : '') + pickerClasses.datePickerRoot}
            InputProps={{
                ...(InputProps || {}),
                classes: {
                    ...(InputProps && InputProps.classes ? InputProps.classes : {}),
                    input: pickerClasses.datePickerInput,
                    adornedEnd: pickerClasses.datePickerAdornment
                }
            }}
        />
    );
});

export default function DateTimeSelector({ value, onChange }) {
    const { timestamps, optimizationPipelineData } = useOptimizationPipeline();
    const classes = useHeaderDateTimeStyles();

    const onChangeRef = useRef(onChange);
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

    const resolution = useMemo(() => {
        return getSummaryResolution(optimizationPipelineData?.summaryDurationUnit);
    }, [optimizationPipelineData]);

    const sortedTimestamps = useMemo(() => {
        return sortTimestampsDesc(timestamps);
    }, [timestamps]);

    const timestampSet = useMemo(() => {
        return new Set(sortedTimestamps.map(ts => getTsCacheKey(ts)).filter(Boolean));
    }, [sortedTimestamps]);

    const {
        allowedDateKeys,
        dateKeyToLatestTsMap,
        dateKeyToHourOptionsMap
    } = useMemo(() => {
        return buildTimestampIndex(sortedTimestamps, resolution);
    }, [sortedTimestamps, resolution]);

    const disabled = sortedTimestamps.length === 0;

    const currentTsString = useMemo(() => {
        return getTsCacheKey(value) || '';
    }, [value]);

    const currentDateValue = useMemo(() => {
        if (!value && !currentTsString) return null;
        return toLocalDayDate(value || currentTsString);
    }, [value, currentTsString]);

    const currentDateKey = useMemo(() => {
        return getDateKey(value || currentTsString);
    }, [value, currentTsString]);

    const currentHourOptions = useMemo(() => {
        if (!currentDateKey) return [];
        return dateKeyToHourOptionsMap[currentDateKey] || [];
    }, [currentDateKey, dateKeyToHourOptionsMap]);

    const currentHourValue = useMemo(() => {
        if (!currentTsString) return '';

        const matched = currentHourOptions.find(option => getTsCacheKey(option.ts) === currentTsString);

        return matched ? matched.ts : '';
    }, [currentTsString, currentHourOptions]);

    const shouldDisableDate = useCallback((date) => {
        if (!date || disabled) return true;

        const dateKey = getDateKey(date);

        return !allowedDateKeys.has(dateKey);
    }, [disabled, allowedDateKeys]);

    useEffect(() => {
        if (disabled) return;

        const newest = sortedTimestamps[0];

        if (!currentTsString || !timestampSet.has(currentTsString)) {
            onChangeRef.current(newest);
        }
    }, [disabled, sortedTimestamps, currentTsString, timestampSet]);

    useEffect(() => {
        if (disabled || resolution !== 'hour') return;
        if (!currentTsString) return;

        const dateKey = getDateKey(currentTsString);
        const options = dateKeyToHourOptionsMap[dateKey] || [];
        const hasCurrentTs = options.some(option => getTsCacheKey(option.ts) === currentTsString);

        if (options.length > 0 && !hasCurrentTs) {
            onChangeRef.current(options[0].ts);
        }
    }, [disabled, resolution, currentTsString, dateKeyToHourOptionsMap]);

    const handleDateChange = useCallback((date) => {
        const parsedDate = parseTsToDate(date);

        if (!parsedDate) {
            onChangeRef.current(null);
            return;
        }

        const dateKey = getDateKey(parsedDate);

        if (resolution === 'hour') {
            const hourOptions = dateKeyToHourOptionsMap[dateKey] || [];

            if (hourOptions.length > 0) onChangeRef.current(hourOptions[0].ts);
            else onChangeRef.current(null);

            return;
        }

        const tsForDay = dateKeyToLatestTsMap[dateKey];

        if (tsForDay) onChangeRef.current(tsForDay);
        else onChangeRef.current(null);
    }, [resolution, dateKeyToLatestTsMap, dateKeyToHourOptionsMap]);

    const handleHourChange = useCallback((event) => {
        const nextTs = event.target.value;

        if (nextTs) onChangeRef.current(nextTs);
        else onChangeRef.current(null);
    }, []);

    const renderTextField = useCallback((props) => (
        <HeaderDateTextField {...props} pickerClasses={classes} />
    ), [classes]);

    return (
        <div className='flex items-start gap-8'>

            {sortedTimestamps.length > 0 && <>
                <div style={{ width: 165 }}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={hu}>
                        <KeyboardDatePicker
                            disableToolbar
                            variant='inline'
                            inputVariant='outlined'
                            format='yyyy.MM.dd.'
                            value={currentDateValue}
                            onChange={handleDateChange}
                            disabled={disabled}
                            shouldDisableDate={shouldDisableDate}
                            autoOk
                            label='Date'
                            InputLabelProps={{ shrink: true }}
                            TextFieldComponent={renderTextField}
                        />
                    </MuiPickersUtilsProvider>
                </div>

                {resolution === 'hour' && (
                    <div style={{ width: 120 }}>
                        <TextField
                            select
                            fullWidth
                            variant='outlined'
                            label='Hour'
                            value={currentHourValue}
                            onChange={handleHourChange}
                            disabled={disabled || currentHourOptions.length === 0}
                            InputLabelProps={{ shrink: true }}
                            className={classes.timeFieldRoot}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <AccessTimeIcon fontSize='small' />
                                    </InputAdornment>
                                )
                            }}
                        >
                            {currentHourOptions.map((option) => (
                                <MenuItem key={option.key} value={option.ts}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>
                )}
            </>}

        </div>
    );
}
